import { Box, createStyles, makeStyles, Mark, Slider, Typography } from '@material-ui/core';
import { isEmpty } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { TextInputTG, TGIosSwitch } from 'views/components/formElements';
import { BlueForceTrackingValue } from 'views/pages/ui/components/settings/commonTypes';

interface BlueForceTrackingFormProps {
  title: string;
  description: string;
  value: BlueForceTrackingValue;
  onChange: (value: NonNullable<BlueForceTrackingValue>) => void;
}

const defaultValue: NonNullable<BlueForceTrackingValue> = {
  enabled: false,
  projectId: '',
  backgroundTimeout: 60,
  locationFrequencyScale: 100,
};

const locationFrequencyScaleMarks: Mark[] = [
  { value: 1, label: 'Less Frequent' },
  { value: 100, label: 'Standard' },
  { value: 200, label: 'More Frequent' },
];

const getSliderAriaValueText = (value: number) => `${value}%`;

export const BlueForceTrackingForm = (props: BlueForceTrackingFormProps) => {
  const { title, value = defaultValue, description, onChange } = props;
  const classes = useStyles();

  const [enabled, setEnabled] = useState(value.enabled ?? defaultValue.enabled);
  const [projectId, setProjectId] = useState(value.projectId ?? defaultValue.projectId);
  const [backgroundTimeout, setBackgroundTimeout] = useState(
    value.backgroundTimeout ?? defaultValue.backgroundTimeout
  );
  const [locationFrequencyScale, setLocationFrequencyScale] = useState(
    value.locationFrequencyScale ?? defaultValue.locationFrequencyScale
  );

  const [locationFrequencyScaleDisplay, setLocationFrequencyScaleDisplay] =
    useState(locationFrequencyScale);

  // const onChangeDebounced = useCallback(debounce(onChange, 600, { leading: true, trailing: true }), []);

  useEffect(() => {
    onChange({ enabled, projectId, backgroundTimeout, locationFrequencyScale });
  }, [enabled, projectId, backgroundTimeout, locationFrequencyScale]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleBackgroundTimeoutChange = useCallback(
    (newValue: string) => {
      !isEmpty(newValue) && setBackgroundTimeout(+newValue);
    },
    [setBackgroundTimeout]
  );

  const handleLocationFrequencyScaleChange = useCallback(
    (_event: ChangeEvent<{}>, newValue: number | number[]) => {
      setLocationFrequencyScaleDisplay(Array.isArray(newValue) ? newValue[0] : newValue);
    },
    [setLocationFrequencyScaleDisplay]
  );

  const handleLocationFrequencyScaleChangeComitted = useCallback(
    (_event: ChangeEvent<{}>, newValue: number | number[]) => {
      setLocationFrequencyScale(Array.isArray(newValue) ? newValue[0] : newValue);
    },
    [setLocationFrequencyScale]
  );

  return (
    <>
      <TGIosSwitch
        title={title}
        description={description}
        checked={enabled}
        onChange={setEnabled}
        otherComponent={
          enabled ? (
            <>
              <Box className={classes.input}>
                <TextInputTG
                  id="projectId"
                  label={'Project ID'}
                  value={projectId}
                  onChange={setProjectId}
                />
              </Box>
              <Box className={classes.input}>
                <TextInputTG
                  id="backgroundTimeout"
                  type="number"
                  label={'Background Location Timeout (Minutes)'}
                  value={backgroundTimeout}
                  onChange={handleBackgroundTimeoutChange}
                  inputProps={{ min: 1 }}
                />
              </Box>
              <Box className={classes.input}>
                <Typography id="symbol-scale-slider" variant="subtitle1">
                  Location Frequency Scale
                </Typography>
                <Slider
                  id="locationFrequencyScale"
                  value={locationFrequencyScaleDisplay}
                  onChange={handleLocationFrequencyScaleChange}
                  onChangeCommitted={handleLocationFrequencyScaleChangeComitted}
                  min={10}
                  max={200}
                  step={10}
                  marks={locationFrequencyScaleMarks}
                  valueLabelDisplay="off"
                  aria-labelledby="symbol-scale-slider"
                  getAriaValueText={getSliderAriaValueText}
                  valueLabelFormat={getSliderAriaValueText}
                  classes={{
                    root: classes.sliderRoot,
                    markLabel: classes.sliderMarkLabel,
                  }}
                />
              </Box>
            </>
          ) : (
            <></>
          )
        }
      />
    </>
  );
};

const useStyles = makeStyles((theme) =>
  createStyles({
    input: {
      paddingTop: 15,
    },
    sliderRoot: {
      // maxWidth: '70%',
    },
    sliderMarkLabel: {
      // Note: If slider marks or value limits change, these may need to be adjusted.
      '&[data-index="0"]': {
        left: '10% !important',
      },
      [`&[data-index="${locationFrequencyScaleMarks.length - 1}"]`]: {
        right: '-40% !important',
      },
    },
  })
);

export default BlueForceTrackingForm;
